import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ApiService } from './api.service';
import { Company } from '../models/company';
import {
  EligibilityErrorCode,
  EligibilityErrorMessages,
  ExecuteEnvelope,
  EXECUTION_FAILED_COPY,
  NETWORK_ERROR_COPY,
  SOURCE_NOT_FOUND_COPY,
  TransferErrorView,
  TransferResult,
  TransferStage,
  TransferValidation,
  ValidateEnvelope,
} from './transfer-borrower.types';

@Injectable({ providedIn: 'root' })
export class TransferBorrowerService {
  readonly stage$        = new BehaviorSubject<TransferStage>(TransferStage.SEARCHING);
  readonly source$       = new BehaviorSubject<Company | null>(null);
  readonly validation$   = new BehaviorSubject<TransferValidation | null>(null);
  readonly acknowledged$ = new BehaviorSubject<boolean>(false);
  readonly lastError$    = new BehaviorSubject<TransferErrorView | null>(null);
  readonly executeResult$ = new BehaviorSubject<TransferResult | null>(null);
  /** True only when the user clicked Submit before checking the ack box. */
  readonly ackError$     = new BehaviorSubject<boolean>(false);

  /** Internal: emits to cancel in-flight validate/submit when reset() runs. */
  private readonly reset$ = new Subject<void>();
  private readonly validateCancel$ = new Subject<void>();

  constructor(private apiService: ApiService) {}

  open(source: Company): void {
    this.source$.next(source);
    this.validation$.next(null);
    this.acknowledged$.next(false);
    this.lastError$.next(null);
    this.ackError$.next(false);
    this.stage$.next(TransferStage.SEARCHING);
  }

  setAcknowledged(value: boolean): void {
    this.acknowledged$.next(value);
    // Checking the box dismisses the "Please check this box" inline error.
    if (value) this.ackError$.next(false);
  }

  clearError(): void {
    this.lastError$.next(null);
  }

  validate(sourceUuid: string, targetUuid: string): void {
    this.validateCancel$.next();

    this.lastError$.next(null);
    this.validation$.next(null);
    this.acknowledged$.next(false);
    this.ackError$.next(false);
    this.stage$.next(TransferStage.VALIDATING);

    this.apiService
      .send('Post', `/api/companies/${sourceUuid}/transfer/validate`, { target_uuid: targetUuid })
      .pipe(
        map((envelope: ValidateEnvelope) => envelope?.response?.objects?.[0]),
        takeUntil(this.validateCancel$),
        takeUntil(this.reset$),
      )
      .subscribe({
        next: (payload: TransferValidation | undefined) => {
          if (payload && payload.eligibility && payload.eligibility.ok) {
            this.validation$.next(payload);
            this.stage$.next(TransferStage.VALIDATED);
            return;
          }
          const code = payload?.eligibility?.errors?.[0]?.code;
          if (!code || !(code in EligibilityErrorMessages)) {
            // Malformed response — treat as network error.
            this.lastError$.next({ code: 'NETWORK_ERROR', copy: NETWORK_ERROR_COPY });
            this.stage$.next(TransferStage.INELIGIBLE);
            return;
          }
          const meta = EligibilityErrorMessages[code as EligibilityErrorCode];
          const view: TransferErrorView = {
            code: code as EligibilityErrorCode,
            copy: meta.copy,
          };
          if (meta.hasViewLink) {
            view.viewBorrowerUuid = targetUuid;
          }
          this.lastError$.next(view);
          this.stage$.next(TransferStage.INELIGIBLE);
        },
        error: (err: unknown) => {
          // eslint-disable-next-line no-console
          console.error('[TransferBorrowerService] validate HTTP error:', err);
          this.lastError$.next({ code: 'NETWORK_ERROR', copy: NETWORK_ERROR_COPY });
          this.stage$.next(TransferStage.INELIGIBLE);
        },
      });
  }

  submit(): void {
    // Double-tap guard. If a submit is already in flight, ignore.
    if (this.stage$.value === TransferStage.EXECUTING) return;

    const validation = this.validation$.value;
    const source     = this.source$.value;
    if (!validation || !source) return;

    // Submit stays enabled even when the ack box is unchecked; gate here
    // instead and surface an inline error the confirmation component renders.
    if (!this.acknowledged$.value) {
      this.ackError$.next(true);
      return;
    }
    this.ackError$.next(false);

    this.lastError$.next(null);
    this.executeResult$.next(null);
    this.stage$.next(TransferStage.EXECUTING);

    this.apiService
      .send('Post', `/api/companies/${source.uuid}/transfer/execute`, {
        target_uuid:  validation.target.uuid,
        acknowledged: true,
      })
      .pipe(
        map((envelope: ExecuteEnvelope) => envelope?.response?.objects?.[0]),
        takeUntil(this.reset$),
      )
      .subscribe({
        next: (result: TransferResult | undefined) => {
          if (!result) {
            // Malformed 200 — treat as generic failure.
            this.lastError$.next({ code: 'NETWORK_ERROR', copy: EXECUTION_FAILED_COPY });
            this.stage$.next(TransferStage.EXECUTION_FAILED);
            return;
          }
          this.executeResult$.next(result);
          this.stage$.next(TransferStage.COMPLETE);
        },
        error: (err: unknown) => {
          // ApiService.handleError strips HttpErrorResponse and re-throws just the
          // response body — so `err` here is the parsed BE body directly (or an
          // arbitrary object for non-HTTP failures). Detect the branch by body
          // shape, not HTTP status. The BE's api_response envelope is the same
          // for success and error: { errors, meta, response: { objects: [<inner>] } }.
          const inner = (err as { response?: { objects?: unknown[] } })
            ?.response?.objects?.[0] as { eligibility?: { errors?: Array<{ code?: string }> }; errorCode?: string } | undefined;

          // Eligibility envelope → state drift → INELIGIBLE
          const code = inner?.eligibility?.errors?.[0]?.code;
          if (code && code in EligibilityErrorMessages) {
            const meta = EligibilityErrorMessages[code as EligibilityErrorCode];
            const view: TransferErrorView = { code: code as EligibilityErrorCode, copy: meta.copy };
            if (meta.hasViewLink) view.viewBorrowerUuid = validation.target.uuid;
            // Clear stale validation so the user must re-validate the new selection.
            this.validation$.next(null);
            this.lastError$.next(view);
            this.stage$.next(TransferStage.INELIGIBLE);
            return;
          }

          // SOURCE_NOT_FOUND → recovery path (transfer may have already
          // succeeded but the response was lost). Reuse COMPLETE with null
          // executeResult$ as the signal.
          if (inner?.errorCode === 'SOURCE_NOT_FOUND') {
            this.lastError$.next({ code: 'SOURCE_NOT_FOUND', copy: SOURCE_NOT_FOUND_COPY });
            this.executeResult$.next(null);
            this.stage$.next(TransferStage.COMPLETE);
            return;
          }

          // Everything else
          this.lastError$.next({ code: 'NETWORK_ERROR', copy: EXECUTION_FAILED_COPY });
          this.stage$.next(TransferStage.EXECUTION_FAILED);
        },
      });
  }

  reset(): void {
    this.reset$.next();
    this.stage$.next(TransferStage.SEARCHING);
    this.source$.next(null);
    this.validation$.next(null);
    this.acknowledged$.next(false);
    this.lastError$.next(null);
    this.executeResult$.next(null);
    this.ackError$.next(false);
  }
}
